import { Component, ElementRef, OnInit, ViewChild } from '@angular/core'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { MatTable, MatTableDataSource } from '@angular/material/table'
import { CouponsService } from '../coupons.service'
import { Observable } from 'rxjs'
import { skip } from 'rxjs/operators'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { VendorService } from 'src/app/multivendor/vendor.service'
import { Router } from '@angular/router'
import { AddonsAccessService, GracePeriodInfo } from 'src/app/addons/addons-subscribtion/addons-access.service'
import { trigger, transition, style, animate } from '@angular/animations'
import { MatDialog } from '@angular/material/dialog'
import { LimitModalComponent } from 'src/app/free-plan/limit-modal/limit-modal.component'
import { environment } from 'src/environments/environment'

export interface Coupon {
  id: string | null
  name: string
  expirationDate: string
  amount: number
  discountType: 'percentage' | 'specific_amount'
  discountCriteria: 'fixed_products_discount' | 'fixed_categories_discount' | 'whole_cart_discount'
  active: boolean
}

@Component({
  selector: 'app-coupons',
  templateUrl: './coupons.component.html',
  styleUrls: ['./coupons.component.scss'],
  animations: [trigger('slideDown', [transition(':enter', [style({ transform: 'translateY(100%)', opacity: 0 }), animate('300ms ease-out', style({ transform: 'translateY(0)', opacity: 1 }))]), transition(':leave', [animate('300ms ease-in', style({ transform: 'translateY(100%)', opacity: 0 }))])])]
})
export class CouponsComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator
  @ViewChild(MatSort) matSort!: MatSort
  @ViewChild(MatTable) table!: MatTable<Coupon>
  @ViewChild('searchInput') searchInput!: ElementRef
  displayedColumns: string[] = ['name', 'expirationDate', 'amount', 'uses', 'discountType', 'discountCriteria', 'active', 'actions']
  dataSource = new MatTableDataSource<Coupon>()
  searchTerm: string = ''
  project_id = this.builderService.selectedProject

  // Delete modal state
  showDeleteModal = false
  couponToDeleteId: string | null = null
  couponToDeleteName: string = ''

  // Grace period properties
  showGracePeriodWarning: boolean = false
  gracePeriodInfo: GracePeriodInfo | null = null
  addonName: string = 'Coupon Management System'

  discountCriteriaMap: { [key: string]: string } = {
    fixed_products_discount: 'Fixed Products Discount',
    fixed_categories_discount: 'Fixed Category Discount',
    whole_cart_discount: 'Whole Cart Discount'
  }
  discountTypeMap: { [key: string]: string } = {
    percentage: 'Percentage',
    specific_amount: 'Specific Amount'
  }
  constructor(
    private couponsService: CouponsService,
    public vendor: VendorService,
    public builderService: BuilderService,
    private addonsAccess: AddonsAccessService,
    private router: Router,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.addonsAccess.loadSubscriptions()
    this.addonsAccess.getSubscriptions$().pipe(skip(1)).subscribe(() => {
      this.checkGracePeriod()
      if (!this.canUseCoupons()) {
        this.forceDisableAllCouponsInFirebase()
      }
    })
    this.loadCoupons()
  }

  ngAfterViewChecked() {
    if (!this.dataSource.paginator && this.paginator) {
      this.dataSource.paginator = this.paginator
    }
  }

      /** Check if addon is in grace period */
  checkGracePeriod() {
    if (this.addonsAccess.isAddonInGracePeriod(this.addonName)) {
      this.gracePeriodInfo = this.addonsAccess.getGracePeriodInfo(this.addonName)
      this.showGracePeriodWarning = true
    } else {
      this.showGracePeriodWarning = false
    }
  }

  /** Dismiss grace period warning */
  dismissGracePeriodWarning() {
    this.showGracePeriodWarning = false
  }

  /** Navigate to upgrade/renewal page */
  navigateToRenew() {
    this.router.navigate(['/billing'])
  }


  /**
   this method call the getCoupons() from couponsService to get the list of the coupons from the database
   @input null
   @returns null
   */
  loadCoupons() {
    this.couponsService.getCoupons().subscribe(
      (coupons: any) => {
        // Transform the data to include id as a property
        const couponsArray: Coupon[] = Object.keys(coupons).map((key) => {
          const coupon = {
            id: key,
            ...coupons[key]
          }
          return coupon
        })
        this.dataSource = new MatTableDataSource<Coupon>(couponsArray)
      },
      (error) => {
        console.error('Error fetching coupons:', error)
      }
    )
  }

  canUseCoupons(): boolean {
    return this.addonsAccess.isAddonActive('All Addons Bundle') || this.addonsAccess.isAddonActive('Coupon Management System')
  }

  validateCoupons() {
    if (this.canUseCoupons()) {
      this.router.navigate(['/coupons/add-coupon'])
      return
    }

    this.openLockedModal()
  }

  openLockedModal() {
    const dialogRef = this.dialog.open(LimitModalComponent, {
      width: '337px',
      maxWidth: '90vw',
      panelClass: 'limit-dialog',
      backdropClass: 'blur-backdrop',
      hasBackdrop: true,
      disableClose: false,
      autoFocus: false,
      data: {
        title: 'Unlock Coupons',
        message: 'Coupons is locked on your plan.',
        cta: 'Unlock Coupons'
      }
    })

    dialogRef.afterClosed().subscribe((upgrade) => {
      if (upgrade) {
        const token = localStorage.getItem('token')
        const params = new URLSearchParams()
        if (token) params.set('token', token)
        params.set('project_id', encodeURIComponent(this.builderService.selectedProject))
        window.open(`${environment.stacksURL}addons/?${params.toString()}`, '_blank')
      }
    })
  }

  /**
   * this method is used to filter the displayed data in the Material table based on the user's input so i can use it to make the search bar.
   * @param filterValue which is a string
   * @returns null
   */
  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase()
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage()
    }
  }

  forceDisableAllCouponsInFirebase() {
  this.couponsService.getCoupons().subscribe((coupons: any[]) => {
    if (!coupons) return

    coupons.forEach((coupon: any) => {
      const couponId = coupon.id
      if (!couponId || couponId === 'undefined') return

      if (coupon.active === true) {
        this.couponsService.updateCoupon(couponId, { ...coupon, active: false }).subscribe(() => {
          console.log(`Coupon ${couponId} forced OFF`)
        })
      }
    })
  })
}

  /**
   *  this method is used to call the delete method from the coupons service so i can use it in the delete button to delete any
   * coupon fron the list of the coupons and form the database.
   * @param couponId which is a string
   * @returns null
   */
  openDeleteModal(couponId: string, couponName: string) {
    this.couponToDeleteId = couponId
    this.couponToDeleteName = couponName
    this.showDeleteModal = true
  }

  closeDeleteModal() {
    this.showDeleteModal = false
    this.couponToDeleteId = null
    this.couponToDeleteName = ''
  }

  confirmDeleteCoupon() {
    if (!this.couponToDeleteId) return
    this.couponsService.deleteCoupon(this.couponToDeleteId).subscribe(
      () => {
        this.loadCoupons()
        this.closeDeleteModal()
      },
      (error) => {
        console.error('Error deleting coupon:', error)
        this.closeDeleteModal()
      }
    )
  }
  hasCoupons(): boolean {
    return this.dataSource.data.length > 0
  }
  activeCoupon(couponId: string, status: boolean): Observable<any> {
    const url = `projects/${this.project_id}/coupons/${couponId}`

    return new Observable<any>((observer) => {
      // First, get the coupon from the database
      this.couponsService.getCoupon(couponId).subscribe(
        (coupon) => {
          // Update the active status of the coupon
          coupon.active = status

          // Log the updated coupon data

          // Save the updated coupon back to the database
          this.couponsService.updateCoupon(couponId, coupon).subscribe(
            (response) => {
              observer.next(response)
              observer.complete()
            },
            (error) => {
              console.error('Error updating active status:', error)
              observer.error(error)
            }
          )
        },
        (error) => {
          console.error('Error fetching coupon:', error)
          observer.error(error)
        }
      )
    })
  }
  toggleActiveStatus(couponId: string, status: boolean) {
    this.activeCoupon(couponId, status).subscribe(
      (response) => {},
      (error) => {
        console.error('Error updating active status:', error)
      }
    )
  }
}
